import { gql } from "@apollo/client";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import { faMicrophoneAdd } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import {
  DSButton,
  DSCircularButton,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import {
  faDownload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import React, {
  useRef,
  useEffect,
  useEffectEvent,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import {
  UserPermissions,
  usePremiumFeature,
} from "../../../hooks/use-premium-feature";
import { alert } from "../../common/Alert";
import RecordingModal from "../../common/modals/RecordingModal";
import PremiumFeatureGate from "../../common/premium/PremiumFeatureGate";

const DELETE_NOTE_AUDIO_RECORDING = gql`
  mutation deleteNoteAudioRecording($noteUuid: String) {
    deleteNoteAudioRecording(noteUuid: $noteUuid) {
      ok
    }
  }
`;

export const GET_AUDIO = gql`
  query getAudio($uuid: String) {
    note(uuid: $uuid) {
      uuid
      jobType
      audioStorageLink
      isNewRecording
      isAutoSoap
      isMarkedAsProcessing
    }
  }
`;

const AudioDownloadLink = ({ audioStorageLink, noteUuid }) => {
  const [loading, setLoading] = useState(false);
  const { showDownloadNoteAudio } = useFlags();

  if (!audioStorageLink || !showDownloadNoteAudio) {
    return null;
  }

  const downloadAudio = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    const a = document.createElement("a");

    try {
      const blob = await fetch(audioStorageLink).then((res) =>
        res.blob(),
      );
      a.href = URL.createObjectURL(blob);
    } catch (error) {
      mixpanel.track("Error downloading audio", {
        error: error.message || error,
        audioStorageLink: audioStorageLink,
        noteUuid: noteUuid,
      });
      alert(
        "error",
        "Failed to download audio. Reload the page and try again or reach out to support.",
      );
      setLoading(false);
      return false;
    }
    a.setAttribute("download", `${noteUuid}.webm`);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setLoading(false);
  };

  return (
    <Tooltip
      title="Click to download your note audio."
      arrow
      placement="top"
    >
      <span>
        <DSCircularButton
          icon={faDownload}
          size="sm"
          onClick={downloadAudio}
          disabled={loading}
        />
      </span>
    </Tooltip>
  );
};

export const AudioPlayer = ({
  noteUuid,
  shouldShowDeleteButton = true,
  shouldShowAddButton = true,
  dataTestId,
}) => {
  const audioRef = useRef();
  const [
    shouldShowDeleteRecordingModal,
    setShouldShowDeleteRecordingModal,
  ] = useState(false);
  const [shouldShowRecordingModal, setShouldShowRecordingModal] =
    useState(false);
  const location = useLocation();

  const pageName = location?.pathname.split("/")[1];
  const isEditingNote =
    pageName === "edit_note" || pageName === "super_soap";

  const [getAudio, { data, refetch }] = useLazyQuery(GET_AUDIO, {
    fetchPolicy: "network-only",
  });

  const [deleteNoteAudioRecording] = useMutation(
    DELETE_NOTE_AUDIO_RECORDING,
  );

  const { hasFeatureAccess } = usePremiumFeature();

  const getAudioEvent = useEffectEvent(() => {
    getAudio({ variables: { uuid: noteUuid } });
  });

  useEffect(() => {
    if (noteUuid) {
      getAudioEvent();
    }
  }, [noteUuid]);

  useEffect(() => {
    if (data?.note?.audioStorageLink) {
      audioRef.current.load();
    }
  }, [data]);

  function hideModal() {
    setShouldShowDeleteRecordingModal(false);
  }

  function renderAudioPlayer() {
    if (!data?.note?.audioStorageLink) {
      return null;
    }
    return (
      <audio
        id="existingRecordingControls"
        className="w-full rounded-lg"
        controls
        ref={audioRef}
      >
        <source src={data.note.audioStorageLink} />
        Your browser does not support the
        <code>audio</code> element.
        <a href={data.note.audioStorageLink} download>
          Click here to download the full appointment audio
        </a>
      </audio>
    );
  }

  return (
    <>
      <RecordingModal
        shouldShow={shouldShowRecordingModal}
        setShouldShow={setShouldShowRecordingModal}
        isForEditNote={isEditingNote}
        noteUuid={noteUuid}
      />
      <DSModal
        isOpen={shouldShowDeleteRecordingModal}
        onClose={hideModal}
        title="Delete Recording"
        dataTestId="delete-recording-modal"
        actions={[
          {
            label: "Never Mind",
            variant: "cancel",
            onClick: hideModal,
            dataTestId: "cancel-button",
          },
          {
            label: "Yes, Delete",
            variant: "primary",
            onClick: () => {
              if (data?.note?.audioStorageLink) {
                deleteNoteAudioRecording({
                  variables: { noteUuid: noteUuid },
                })
                  .then(() => {
                    alert(
                      "success",
                      "Audio recording deleted successfully.",
                    );
                    refetch({ uuid: noteUuid });
                  })
                  .catch(() => {
                    alert(
                      "error",
                      "An error occurred. Please try again later or contact support.",
                    );
                  });
              }
              hideModal();
            },
            dataTestId: "confirm-button",
          },
        ]}
      >
        <h5>
          Are you sure you&apos;d like to{" "}
          <strong className="text-red-500">
            delete the recording
          </strong>{" "}
          associated with this note? This can&apos;t be undone.
        </h5>
      </DSModal>
      <div className="flex flex-row items-center space-x-1">
        {renderAudioPlayer()}
        <AudioDownloadLink
          audioStorageLink={data?.note?.audioStorageLink}
          noteUuid={data?.note?.uuid}
        />
        {shouldShowAddButton &&
          data?.note?.audioStorageLink &&
          (data?.note?.jobType === "template" &&
          !hasFeatureAccess(
            UserPermissions.ADAPTIVE_TEMPLATES_ENABLED,
          ) ? (
            <PremiumFeatureGate
              featureName="Adaptive Templates"
              iconPosition="top-right"
              iconSize="sm"
              requiredPermission={
                UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
              }
            >
              <DSButton icon={faMicrophoneAdd} size="sm">
                Add
              </DSButton>
            </PremiumFeatureGate>
          ) : (
            <Tooltip
              title="Click to make a follow-up recording. It will be combined with the original recording."
              arrow
              placement="top"
            >
              <span>
                <DSButton
                  icon={faMicrophoneAdd}
                  onClick={() => setShouldShowRecordingModal(true)}
                  dataTestId={dataTestId}
                >
                  Add
                </DSButton>
              </span>
            </Tooltip>
          ))}
        {data?.note?.audioStorageLink &&
          !data?.note?.isAutoSoap &&
          !data?.note?.isNewRecording &&
          !data?.note?.isMarkedAsProcessing &&
          shouldShowDeleteButton && (
            <button
              title="Delete recording"
              className="text-sm h-10 px-4 flex items-center justify-center bg-gray-400 hover:bg-red-400 rounded-full shadow-xs transition-colors"
              onClick={() => setShouldShowDeleteRecordingModal(true)}
            >
              <FontAwesomeIcon icon={faTimes} color="white" />
            </button>
          )}
      </div>
    </>
  );
};
